<template>
    <user-dashboard-layout>
        <v-data-table
            v-if="categories.data"
            :loading="loading"
            :page="categories.meta.current_page"
            :items-per-page="categories.meta.per_page * 1"
            :server-items-length="categories.meta.total"
            @pagination="paginate"
            :headers="headers"
            :items="categories.data"
            class="elevation-1"
        >
            <template v-slot:header="{ props }">
                <thead>
                <tr>
                    <th v-for="header in props.headers" :key="header.text">
                        {{ $t('general.' + header.text.toLowerCase()) }}
                    </th>
                </tr>
                </thead>
            </template>
            <template #body="props">
                <draggable
                    :disabled="!sortingMode"
                    item-key="id"
                    :list="props.items"
                    tag="tbody"
                    @start="onStartDrag"
                    @end="onEndDrag(props.items)"
                    :class="{ 'sorting-enabled': sortingMode }"
                >
                    <tr v-for="(item, index) in props.items" :key="index">
                        <td>
                            {{ item.name }}
                        </td>
                        <td>
                            {{ Math.trunc(item.extra_processing_time) }}
                        </td>
                        <td>
                            {{ item.position }}
                        </td>
                        <td>
                            <v-icon small class="mr-2" @click="editItem(item)">
                                mdi-pencil
                            </v-icon>
                            <v-icon small @click="deleteItem(item)">
                                mdi-delete
                            </v-icon>
                        </td>
                    </tr>
                </draggable>
            </template>
            <template v-slot:top>
                <v-toolbar flat>
                    <v-toolbar-title>{{ $t('general.my-category') }}</v-toolbar-title>
                    <v-divider class="mx-4" inset vertical></v-divider>
                    <v-spacer></v-spacer>
                    <v-dialog v-model="dialog" max-width="500px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                color="primary"
                                dark
                                class="mb-2 ml-2"
                                v-bind="attrs"
                                v-on="on"
                            >
                                {{ $t('general.new-item') }}
                            </v-btn>
                            <v-btn
                                color="primary"
                                dark
                                class="mb-2"
                                :outlined="sortingMode"
                                :depressed="!sortingMode"
                                :class="{ 'v-btn--active': sortingMode }"
                                @click="sortingMode = !sortingMode"
                            >
                                {{ $t('general.sort') }}
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-title>
                                <span class="text-h5">{{ formTitle }}</span>
                            </v-card-title>

                            <v-card-text>
                                <v-container>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="category.name"
                                                outlined
                                                dense
                                                :label="$t('general.category-name')"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="category.extra_processing_time"
                                                type="number"
                                                min="0"
                                                value="0"
                                                step="1"
                                                outlined
                                                dense
                                                :label="$t('general.extra_processing_time')"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" class="py-0">
                                            <div class="title-field">{{ $t('general.position') }}</div>
                                            <v-text-field
                                                v-model.number="category.position"
                                                type="number"
                                                step="1"
                                                outlined
                                                dense
                                                :rules="[value => value === null || value >= 0 || $t('general.position-must-be-positive')]"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card-text>

                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="blue darken-1"
                                    text
                                    @click="close"
                                >
                                    {{ $t('general.cancel') }}
                                </v-btn>
                                <v-btn color="blue darken-1" text @click="save">
                                    {{ $t('general.save') }}
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                    <v-dialog v-model="dialogDelete" max-width="500px">
                        <v-card>
                            <v-card-title class="text-h5"
                            >{{ $t('general.are-you-sure-you-want') }}
                            </v-card-title
                            >
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="blue darken-1"
                                    text
                                    @click="closeDelete"
                                >{{ $t('general.cancel') }}
                                </v-btn
                                >
                                <v-btn
                                    color="blue darken-1"
                                    text
                                    @click="deleteItemConfirm"
                                >{{ $t('general.ok') }}
                                </v-btn
                                >
                                <v-spacer></v-spacer>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-toolbar>
            </template>
        </v-data-table>
    </user-dashboard-layout>
</template>

<script>
import UserDashboardLayout from "../../../layouts/UserDashboardLayout";
import {mapActions, mapGetters, mapMutations} from "vuex";
import draggable from "vuedraggable";
import debounce from 'lodash/debounce';

export default {
    components: {draggable, UserDashboardLayout},
    data: () => ({
        sortingMode: false,
        loading: false,
        dialog: false,
        dialogDelete: false,
        headers: [
            {
                text: "Name",
                align: "start",
                sortable: false,
                value: "name"
            },
            {
                text: "extra_processing_time",
                value: "extra_processing_time"
            },
            {
                text: "position",
                value: "position"
            },
            {
                text: "Actions",
                value: "actions"
            }
        ],
        editedIndex: -1,
        pagination: {
            page: 1,
            itemsPerPage: 5
        },
        debounceInit: Function,
    }),

    computed: {
        ...mapGetters(["defaultCategory", "category", "categories"]),
        formTitle() {
            return this.editedIndex === -1 ? "New Item" : "Edit Item";
        }
    },

    watch: {
        dialog(val) {
            val || this.close();
        },
        dialogDelete(val) {
            val || this.closeDelete();
        },
        sortingMode(val) {
            if (val) {
                this.pagination = {
                    page: 1,
                    itemsPerPage: -1,
                }
                this.init();
            } else {
                this.pagination = {
                    page: 1,
                    itemsPerPage: 5
                }
                this.init();
            }
        }
    },
    mounted() {
        this.debounceInit = debounce(this.init, 1000);
        this.init();
    },

    methods: {
        ...mapActions([
            "storeCategory",
            "setCategories",
            "updateCategory",
            "destroyCategory",
            "sorting",
        ]),
        ...mapMutations(["setCategory"]),

        async init() {
            this.loading = true;
            await this.setCategories(this.pagination);
            this.loading = false;
        },
        editItem(item) {
            this.editedIndex = this.categories.data.findIndex(
                i => i.id === item.id
            );
            this.setCategory(item);
            this.dialog = true;
        },

        deleteItem(item) {
            this.editedIndex = this.categories.data.findIndex(
                i => i.id === item.id
            );
            this.setCategory(item);
            this.dialogDelete = true;
        },

        async deleteItemConfirm() {
            await this.destroyCategory(this.category);
            this.init();
            this.closeDelete();
        },

        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.setCategory(this.defaultCategory);
                this.editedIndex = -1;
            });
        },

        closeDelete() {
            this.dialogDelete = false;
            this.$nextTick(() => {
                this.setCategory(this.defaultCategory);
                this.editedIndex = -1;
            });
        },

        async save() {
            if (this.editedIndex > -1) {
                await this.updateCategory(this.category);
                this.init();
            } else {
                await this.storeCategory(this.category);
                this.init();
            }
            this.close();
        },
        paginate(pagination) {
            this.pagination = pagination;
            this.setCategories(pagination);
        },
        async changeSortItems(items) {
            let data = items.map((item, index) => {
                return {
                    id: item.id,
                    position: index,
                }
            });
            await this.sorting(data)
            this.debounceInit()
        },
        onStartDrag() {
            // todo
        },
        onEndDrag(items) {
            this.changeSortItems(items)
        }
    }
};
</script>

<style>
thead.v-data-table-header {
    display: none !important;
}

.sorting-enabled tr {
    cursor: grab; /* показывает, что можно перетаскивать */
}

.sorting-enabled tr:active {
    cursor: grabbing; /* при зажатии */
}
</style>
